import { useEffect, useRef, useState } from "react";
import { animate, createTimeline } from "animejs";
import { dissolveConfig } from "../lib/dissolveConfig";
import { getLenis } from "../lib/lenis";
import {
  MOBILE_BREAKPOINT_PX,
  NARROW_MOBILE_BREAKPOINT_PX,
  SECTION_BUFFER_VH,
  pickConfig,
} from "../lib/tacConfig";
import type { CylinderCarouselHandle } from "./CylinderCarousel";

interface Props {
  carouselRef: React.RefObject<CylinderCarouselHandle | null>;
}

const HEADING = "Times are changing…";
const HEADING_ITALICS = ["changing"];

const STATEMENTS: Array<{
  text: string;
  italics: string[];
  side: "left" | "right";
  rowClass: string;
  gif: { src: string; cls: string };
}> = [
  {
    text: "AI tools make building things feel like magic.",
    italics: ["magic"],
    side: "right",
    rowClass: "tac2-statement--row1",
    gif: { src: "/assets/magic.mp4", cls: "tac2-gif--magic" },
  },
  {
    text: "To create and test an idea is simply a prompt away.",
    italics: ["create", "test"],
    side: "left",
    rowClass: "tac2-statement--row2",
    gif: { src: "/assets/coding.mp4", cls: "tac2-gif--coding" },
  },
  {
    text: "Your design team needs to join you in this new way of building.",
    italics: [],
    side: "right",
    rowClass: "tac2-statement--row3",
    gif: { src: "/assets/graphic-design.mp4", cls: "tac2-gif--design" },
  },
];

// Terminal line, pre-tokenized so create / test / magic can render in
// Calendas Plus italic with the iridescent mesh fill — a callback to the
// italics in the boxes above.
const TERMINAL_TOKENS: Array<{ text: string; italic: boolean }> = [
  { text: "We use AI to ", italic: false },
  { text: "create", italic: true },
  { text: " and ", italic: false },
  { text: "test", italic: true },
  { text: " things that feel like ", italic: false },
  { text: "magic", italic: true },
];
const TERMINAL_TOTAL_CHARS = TERMINAL_TOKENS.reduce(
  (n, t) => n + t.text.length,
  0,
);

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeOut(p: number): number {
  return 1 - Math.pow(1 - p, 2);
}

function stripPunct(s: string): string {
  return s.replace(/[.,…!?]/g, "");
}

function progressIn(sectionProg: number, range: readonly [number, number]): number {
  return clamp01((sectionProg - range[0]) / (range[1] - range[0]));
}

const CANVAS_W = 1512;
const CANVAS_H = 3094;
const STORY_H = 2466;

// Path geometry exported directly from Sketch ("Second secion" artboard).
const PATH_SEGMENTS = [
  "M1255.13 220.36 C1420.60 220.36 1410.10 396.20 1274.75 396.20 C1041.98 396.20 1004.5 451.71 1004.5 555.77",
  "M1004.5 825 C1004.5 1415.40 507.5 749.89 507.5 1257.99",
  "M507.5 1536.41 C507.5 1905 1004.5 1457.44 1004.5 1816.95",
  "M1004.5 2106.5 C1004.5 2450.54 756 2162.41 756 2450.54",
];

// Each path's authored Y endpoints + which anchor each end should pin to.
// At narrow widths boxes grow taller (text wrap), so their actual top/bottom
// drift from the Sketch artboard positions and the paths overshoot into
// the next box (tail covered) or start below the previous box (gap). We
// remap each path's Y range to the *measured* edges via an SVG transform
// applied to the <path> element (X scale stays 1; only Y scales+translates).
const PATH_Y_ANCHORS: Array<{
  origStart: number;
  origEnd: number;
  startAnchor: "headingBottom" | "box0Bottom" | "box1Bottom" | "box2Bottom";
  endAnchor: "box0Top" | "box1Top" | "box2Top" | "terminalTop";
}> = [
  { origStart: 220.36, origEnd: 555.77, startAnchor: "headingBottom", endAnchor: "box0Top" },
  { origStart: 825, origEnd: 1257.99, startAnchor: "box0Bottom", endAnchor: "box1Top" },
  { origStart: 1536.41, origEnd: 1816.95, startAnchor: "box1Bottom", endAnchor: "box2Top" },
  { origStart: 2106.5, origEnd: 2450.54, startAnchor: "box2Bottom", endAnchor: "terminalTop" },
];

// Vertical centers of each anchor element in canvas (artboard) units.
// Artboard-fixed; does not vary by breakpoint.
const ANCHOR_Y_ART: Record<string, number> = {
  heading: 193, // 129 + 128/2
  box0: 686, // 566 + 240/2
  box1: 1394, // 1274 + 240/2
  box2: 1961, // 1841 + 240/2
  terminal: 2780, // 2466 + 628/2
};

// Breakpoint-dependent constants (ANCHOR_VH, PHASE_PLAN, derived CUM_FRAC,
// WAYPOINTS, T_*, IMPACT_FRAC, T_DISSOLVE) are computed inside the
// cinematic effect from `pickConfig(window.innerWidth)`. The effect re-runs
// when the viewport crosses MOBILE_BREAKPOINT_PX.
// Typing exactly fills the type-terminal phase; the short hold-magic
// phase that follows is a beat where the completed line sits before
// the wand swoops in.
const TERMINAL_TYPING_PORTION = 1.0;
// How big the terminal scales during the zoom — large enough to overflow
// the 100vh stage and visually fill the viewport.
const ZOOM_MAX_SCALE = 6;

// Filter parameters and dissolve animation values live in
// src/lib/dissolveConfig.ts. Read via `dissolveConfig.*` in update().

// Wand tip position inside the asset, as fractions of its bounding box.
// Measured from assets/wand-hand.png by scripts/measure-wand-tip.ts —
// the topmost opaque pixels sit at ~95% width × 1% height. Re-measure
// (and update these constants) if the wand asset changes.
const WAND_TIP_X = 0.954;
const WAND_TIP_Y = 0.01;

const WORD_REVEAL_THRESHOLD = 0.95;

export function TimesAreChanging({ carouselRef }: Props) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" &&
    window.innerWidth >= MOBILE_BREAKPOINT_PX,
  );
  // Tracks the narrow-mobile threshold (≤425) so pickConfig swaps to
  // narrowMobileConfig and the cinematic re-runs when crossing 425.
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" &&
    window.innerWidth <= NARROW_MOBILE_BREAKPOINT_PX,
  );

  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT_PX}px)`);
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(
      `(max-width: ${NARROW_MOBILE_BREAKPOINT_PX}px)`,
    );
    const onChange = () => setIsNarrow(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const sectionRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const headingWordRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const boxRefs = useRef<Array<HTMLDivElement | null>>([null, null, null]);
  const boxWordRefs = useRef<Array<Array<HTMLSpanElement | null>>>([[], [], []]);
  const segRefs = useRef<Array<SVGPathElement | null>>([null, null, null, null]);
  const gifRefs = useRef<(HTMLVideoElement | null)[]>([null, null, null]);

  const terminalTokenRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const terminalBlockRef = useRef<HTMLDivElement | null>(null);
  const terminalRef = useRef<HTMLDivElement | null>(null);
  const wandRef = useRef<HTMLDivElement | null>(null);
  const promptRef = useRef<HTMLDivElement | null>(null);
  const dissolveMapRef = useRef<SVGFEDisplacementMapElement | null>(null);
  // Big-noise turbulence — its `seed` is reseeded on every forward entry
  // into the dissolve range so the dust pattern varies between plays
  // (matches the codepen's per-fire reseed).
  const bigNoiseRef = useRef<SVGFETurbulenceElement | null>(null);
  // Tracks the previous frame's scroll-derived dissolve progress so we
  // can detect the forward boundary (0 → >0) and reseed exactly once.
  const lastDissolveProgRef = useRef<number>(0);

  // Two peer timelines orchestrated imperatively:
  //   cinematicTl    — wand/dissolve/zoom. Plays on every forward trigger
  //                    and reverses on every reverse trigger. Drives
  //                    progressProxyRef.current.t, which update() reads
  //                    each frame as sectionProg for the [CUM_FRAC[10], 1]
  //                    sub-range.
  //   postCinematicTl — .sub opacity 0→1 + carousel un-furl. Plays ONLY
  //                    on the very first forward trigger; never reversed,
  //                    never replayed. The carousel intro is one-shot.
  //
  // After the first forward, carouselIntroDoneRef flips true. From then
  // on, the .sub crossfade on every forward/reverse cycle is handled by
  // a fire-and-forget animate() (subFadeAnimRef), and the carousel stays
  // at setEntryProgress(1) — continuous spin, never re-scrubbed.
  const cinematicTlRef = useRef<ReturnType<typeof createTimeline> | null>(null);
  const postCinematicTlRef = useRef<ReturnType<typeof createTimeline> | null>(null);
  const postCinematicProgressRef = useRef<{ p: number }>({ p: 0 });
  const subFadeAnimRef = useRef<ReturnType<typeof animate> | null>(null);
  const carouselIntroDoneRef = useRef(false);
  const progressProxyRef = useRef<{ t: number }>({ t: 0 });
  const triggeredRef = useRef(false);
  const releaseParkRef = useRef<(() => void) | null>(null);
  const releaseReverseTriggerRef = useRef<(() => void) | null>(null);
  // After a reverse completes, ignore forward-trigger attempts for this
  // long. Stops trackpad momentum / scroll twitches from instantly
  // re-firing the cinematic the user just escaped from.
  const cooldownUntilRef = useRef(0);

  useEffect(() => {
    const config = pickConfig(window.innerWidth);
    const { anchorVH: ANCHOR_VH, phasePlan: PHASE_PLAN } = config;

    const TOTAL_PHASE_VH = PHASE_PLAN.reduce((n, p) => n + p.dur, 0);
    const SECTION_VH = TOTAL_PHASE_VH + SECTION_BUFFER_VH;

    // Cumulative fractions of section progress at each phase boundary.
    // Length = PHASE_PLAN.length + 1. CUM_FRAC[i] is the start of phase i;
    // CUM_FRAC[PHASE_PLAN.length] = 1.
    const CUM_FRAC: number[] = (() => {
      const out = [0];
      for (const p of PHASE_PLAN) out.push(out[out.length - 1] + p.dur / TOTAL_PHASE_VH);
      return out;
    })();

    // Build the "currently parked anchor" at each phase boundary. The
    // canvas Y-translation is interpolated between consecutive waypoints.
    // Stays anchored on the terminal through hold/wand/zoom/dark phases.
    const anchorsAtBoundary: string[] = ["heading"];
    {
      let current = "heading";
      for (const p of PHASE_PLAN) {
        if (p.kind === "park-heading") current = "heading";
        else if (p.kind === "park-box" && p.idx != null) current = `box${p.idx}`;
        else if (p.kind === "path" && p.idx != null) {
          current = p.idx < 3 ? `box${p.idx}` : "terminal";
        } else current = "terminal";
        anchorsAtBoundary.push(current);
      }
    }
    const WAYPOINTS = anchorsAtBoundary.map((a, i) => ({ p: CUM_FRAC[i]!, a }));

    // Helper to look up the [start, end] fractional range of a phase by
    // its kind (+ optional idx). Lets us avoid magic numeric indices into
    // CUM_FRAC for the per-animation ranges. CUM_FRAC[N] by index is
    // still used for the wand-trigger / reverse-target plumbing below.
    const phaseRange = (kind: string, idx?: number): readonly [number, number] => {
      const i = PHASE_PLAN.findIndex(
        (p) => p.kind === kind && (idx == null || p.idx === idx),
      );
      return [CUM_FRAC[i]!, CUM_FRAC[i + 1]!] as const;
    };

    const T_HEADING = phaseRange("park-heading");
    const T_PATH = [
      phaseRange("path", 0),
      phaseRange("path", 1),
      phaseRange("path", 2),
      phaseRange("path", 3),
    ] as const;
    const T_BOX = [
      phaseRange("park-box", 0),
      phaseRange("park-box", 1),
      phaseRange("park-box", 2),
    ] as const;
    const T_TERMINAL = phaseRange("type-terminal");
    const T_WAND = phaseRange("wand-tap");
    const T_ZOOM = phaseRange("zoom");
    // Dissolve runs from the right-snap moment of the wand strike (mid-
    // recoil, ~92.5% through the wand-tap range) through the post-tap
    // buffer and into the first 70% of the zoom phase so you fly past
    // the still-scattering dust as the terminal grows.
    const IMPACT_FRAC = T_WAND[0] + 0.925 * (T_WAND[1] - T_WAND[0]);
    const T_DISSOLVE = [
      IMPACT_FRAC,
      T_ZOOM[0] + 0.7 * (T_ZOOM[1] - T_ZOOM[0]),
    ] as const;

    // Apply section height for the active breakpoint so the scrollable
    // distance matches the phase plan's pacing.
    const sectionEl = sectionRef.current;
    if (sectionEl) {
      sectionEl.style.setProperty("--tac2-section-vh", String(SECTION_VH));
    }

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      const allWords: (HTMLSpanElement | null)[] = [
        ...headingWordRefs.current,
        ...boxWordRefs.current.flat(),
      ];
      for (const el of allWords) {
        if (!el) continue;
        el.style.opacity = "1";
        el.style.filter = "none";
        el.classList.add("is-revealed");
      }
      for (const seg of segRefs.current) {
        if (!seg) continue;
        // Fully drawn — remove dash pattern entirely so vector-effect's
        // viewport-vs-user-unit difference doesn't matter.
        seg.style.strokeDasharray = "none";
        seg.style.strokeDashoffset = "0";
      }
      for (let i = 0; i < gifRefs.current.length; i++) {
        const img = gifRefs.current[i];
        if (!img) continue;
        img.style.clipPath = "inset(0 0 0 0)";
      }
      for (const boxEl of boxRefs.current) {
        if (boxEl) {
          boxEl.style.clipPath = "inset(-10px -10px -10px -10px)";
        }
      }
      // Show the full terminal line.
      for (let i = 0; i < TERMINAL_TOKENS.length; i++) {
        const tok = TERMINAL_TOKENS[i];
        const el = terminalTokenRefs.current[i];
        if (!el || !tok) continue;
        el.textContent = tok.text;
      }
      return;
    }

    // getTotalLength() returns the path length in *user units* (the SVG's
    // 1512×2466 viewBox). With vector-effect: non-scaling-stroke, however,
    // stroke-dasharray and stroke-dashoffset are interpreted in *viewport*
    // (rendered) pixels. A flat `userLen * (renderedW / CANVAS_W)` scale
    // is only correct when the path container preserves aspect ratio —
    // at narrow breakpoints we stretch `.tac2-path` vertically (height
    // 95% vs base 79.7%) with preserveAspectRatio="none", so Y scales
    // ~1.19× more than X and the heuristic under-reports the rendered
    // length, leaving a tail of each path visible at prog=0. Sample each
    // path through getScreenCTM() to get the actual rendered length in
    // viewport px. Recompute on width change (cheap: 64 samples × 4 paths).
    // Compute viewBox-Y coordinates of heading-bottom / box-top / box-bottom /
    // terminal-top by measuring DOM rects relative to the path SVG. Returns
    // null if any element isn't laid out yet.
    const measureAnchors = (): Record<string, number> | null => {
      const canvas = canvasRef.current;
      const pathSvg = segRefs.current[0]?.ownerSVGElement;
      if (!canvas || !pathSvg) return null;
      const cR = canvas.getBoundingClientRect();
      const sR = pathSvg.getBoundingClientRect();
      if (sR.height <= 0) return null;
      const svgTopInCanvas = sR.top - cR.top;
      const svgH = sR.height;
      const toY = (yPxFromCanvasTop: number) =>
        ((yPxFromCanvasTop - svgTopInCanvas) / svgH) * STORY_H;

      const getRect = (el: HTMLElement | null) => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { top: r.top - cR.top, bottom: r.bottom - cR.top };
      };
      const heading = getRect(headingRef.current);
      const box0 = getRect(boxRefs.current[0]);
      const box1 = getRect(boxRefs.current[1]);
      const box2 = getRect(boxRefs.current[2]);
      const terminal = getRect(terminalBlockRef.current);
      if (!heading || !box0 || !box1 || !box2 || !terminal) return null;
      return {
        headingBottom: toY(heading.bottom),
        box0Top: toY(box0.top),
        box0Bottom: toY(box0.bottom),
        box1Top: toY(box1.top),
        box1Bottom: toY(box1.bottom),
        box2Top: toY(box2.top),
        box2Bottom: toY(box2.bottom),
        terminalTop: toY(terminal.top),
      };
    };

    const SAMPLES = 64;
    const measureRenderedLength = (sp: SVGPathElement): number => {
      const userLen = sp.getTotalLength();
      if (userLen === 0) return 0;
      const ctm = sp.getScreenCTM();
      if (!ctm) return userLen;
      let prev = sp.getPointAtLength(0).matrixTransform(ctm);
      let acc = 0;
      for (let i = 1; i <= SAMPLES; i++) {
        const p = sp.getPointAtLength((userLen * i) / SAMPLES).matrixTransform(ctm);
        acc += Math.hypot(p.x - prev.x, p.y - prev.y);
        prev = p;
      }
      return acc;
    };
    const renderedLenCache = { width: -1, lengths: [0, 0, 0, 0] as number[] };

    let raf = 0;
    let lastTypedChars = -1;

    const update = () => {
      raf = 0;
      const section = sectionRef.current;
      const stage = stageRef.current;
      const canvas = canvasRef.current;
      if (!section || !stage || !canvas) return;

      const vh = window.innerHeight;
      const sectRect = section.getBoundingClientRect();
      const sectionH = sectRect.height;
      const scrollableH = sectionH - vh;
      if (scrollableH <= 0) return;

      const scrollSectionProg = clamp01(-sectRect.top / scrollableH);

      // Broken-state recovery. If the user fast-scrolled up past parked
      // in a single wheel event, the reverse trigger's onWheel handler
      // would have seen isParked() === false at the moment of the event
      // and returned without firing. The cinematic timeline is still in
      // "completed forward" state (proxy.t = 1), so without intervention
      // the next frame would jump sectionProg from 1.0 to scrollSectionProg
      // — a visible jolt that leaks the paper background at the bottom.
      // Snap the cinematic back to fresh state so the transition across
      // the CUM_FRAC[10] boundary is continuous (proxy.t = 0 makes the
      // proxy formula equal CUM_FRAC[10] at the boundary).
      if (
        triggeredRef.current &&
        scrollSectionProg < CUM_FRAC[10]! &&
        progressProxyRef.current.t > 0 &&
        cinematicTlRef.current
      ) {
        progressProxyRef.current.t = 0;
        cinematicTlRef.current.pause();
        cinematicTlRef.current.seek(0);
        triggeredRef.current = false;
        releaseParkRef.current?.();
        releaseParkRef.current = null;
        releaseReverseTriggerRef.current?.();
        releaseReverseTriggerRef.current = null;
        subFadeAnimRef.current?.pause();
        subFadeAnimRef.current = null;
        const subEl = document.querySelector(".sub");
        if (subEl instanceof HTMLElement) {
          subEl.style.opacity = "0";
          subEl.classList.remove("is-content-revealed");
        }
        cooldownUntilRef.current = performance.now() + 400;
      }

      // Void-state auto-fire. The wheel listener in installReverseTrigger
      // only fires while a wheel event is in flight. If the user wheels
      // up briefly and Lenis carries the scroll past parked via inertia
      // (with no wheel events firing during that tail), the trigger
      // never sees an event inside the parked window — and the user ends
      // up stuck with a dark void above the carousel. Detect that void
      // here (sub has been pushed DOWN past parked while the cinematic
      // is still in its completed-forward state) and fire the reverse
      // animation automatically as a safety net.
      if (
        triggeredRef.current &&
        progressProxyRef.current.t === 1 &&
        releaseReverseTriggerRef.current
      ) {
        const subElForVoid = document.querySelector(".sub");
        if (
          subElForVoid &&
          subElForVoid.getBoundingClientRect().top > REVERSE_AUTO_TRIGGER_PX
        ) {
          fireReverse();
        }
      }

      // Forward trigger: the moment the user's scroll crosses the
      // wand-tap threshold for the first time (and we're not in a
      // post-reverse cooldown), snap them to the SETTLED viewport
      // (sectionProg ≈ 0.995) and park there.
      //
      // Why snap forward to ~1.0 instead of parking at the trigger Y:
      // `.sub`'s `margin-top: -100vh` means `.sub`'s top aligns with
      // `.tac2`'s bottom MINUS 100vh. Only at sectionProg = 1.0 does
      // that put `.sub`'s top at viewport top — i.e., the carousel
      // (top of `.sub`) at the top of the viewport. Parking at the
      // trigger Y (sectionProg ≈ 0.815) would leave the carousel
      // ~87vh below viewport top — at the bottom of the screen / off-
      // screen — and the user would see a huge dark gap above it.
      //
      // The sticky `.tac2-stage` (top:0; height:100vh) keeps the wand /
      // dissolve / terminal-zoom visuals rendering at the top of the
      // viewport regardless of scrollY within `.tac2`'s scroll range,
      // so this snap doesn't disrupt the cinematic at all. update()'s
      // sectionProg derivation reads progressProxyRef.current.t (timeline-
      // driven), not scrollSectionProg, once triggered — so the visuals
      // start at sectionProg = CUM_FRAC[10] and progress to 1.0 with
      // anime.js easing while the user is held at the settled Y.
      if (
        !triggeredRef.current &&
        scrollSectionProg >= CUM_FRAC[10]! &&
        performance.now() >= cooldownUntilRef.current &&
        cinematicTlRef.current
      ) {
        triggeredRef.current = true;
        const sectTopPageY = window.scrollY + sectRect.top;
        const settledY = sectTopPageY + (1 - 0.005) * scrollableH;
        const lenis = getLenis();
        if (lenis) lenis.scrollTo(settledY, { immediate: true });
        else window.scrollTo(0, settledY);
        releaseParkRef.current = engagePark(settledY);
        cinematicTlRef.current.play();
      }

      // Pre-CUM_FRAC[10]: use raw scroll (heading/boxes/paths/typing).
      // From CUM_FRAC[10] onward: use the cinematic timeline's eased
      // progress, remapped back to sectionProg space. At the boundary
      // both values are equal (timeline t=0 ⟹ sectionProg=CUM_FRAC[10]),
      // so the handover has no discontinuity.
      const sectionProg =
        scrollSectionProg < CUM_FRAC[10]!
          ? scrollSectionProg
          : CUM_FRAC[10]! +
            progressProxyRef.current.t * (1 - CUM_FRAC[10]!);

      // Compute canvas Y translation by interpolating between waypoints.
      // anchorPx returns the canvas translateY needed to put the anchor's
      // center at its target viewport position.
      const stageRect = stage.getBoundingClientRect();
      const canvasH = stageRect.width * (CANVAS_H / CANVAS_W);
      // Heading is a child of .tac2-canvas (artboard y≈193), so it
      // translates with the canvas. The "heading" anchor parks the canvas
      // such that the rendered heading center sits at ANCHOR_VH.heading
      // of the viewport; subsequent phases translate the canvas upward,
      // sliding the heading off-screen as the path/boxes come into view.
      const anchorPx = (name: string): number => {
        const yArt = ANCHOR_Y_ART[name] ?? 0;
        const yPx = yArt * (canvasH / CANVAS_H);
        return (ANCHOR_VH[name] ?? 0.5) * vh - yPx;
      };

      let canvasY = 0;
      for (let i = 0; i < WAYPOINTS.length - 1; i++) {
        const a = WAYPOINTS[i]!;
        const b = WAYPOINTS[i + 1]!;
        if (sectionProg >= a.p && sectionProg <= b.p) {
          const t = b.p === a.p ? 0 : (sectionProg - a.p) / (b.p - a.p);
          canvasY = lerp(anchorPx(a.a), anchorPx(b.a), easeOut(t));
          break;
        }
      }
      canvas.style.transform = `translate3d(0, ${canvasY}px, 0)`;

      // Heading reveal
      const headingProg = progressIn(sectionProg, T_HEADING);
      const headingEased = easeOut(headingProg);
      // Stagger words slightly inside the heading so it has a wave feel.
      {
        const words = headingWordRefs.current;
        const n = words.length;
        const stagger = 0.18;
        const stretch = 1 + stagger * Math.max(n - 1, 0);
        for (let wi = 0; wi < n; wi++) {
          const el = words[wi];
          if (!el) continue;
          const wp = clamp01(headingProg * stretch - wi * stagger);
          const e = easeOut(wp);
          el.style.opacity = String(0.16 + 0.84 * e);
          el.style.filter = `blur(${(1 - e) * 9}px)`;
          if (e >= WORD_REVEAL_THRESHOLD) el.classList.add("is-revealed");
          else el.classList.remove("is-revealed");
        }
      }
      void headingEased;

      // Box reveals + GIFs. Both clip-paths are driven directly from
      // boxProg every frame instead of via a one-shot CSS keyframe with
      // forwards fill, so scrolling back smoothly reverses the reveal
      // (cards un-clip back to their hidden state) instead of staying
      // stuck in the revealed state.
      const boxProgsDbg: number[] = [0, 0, 0];
      for (let bi = 0; bi < 3; bi++) {
        const range = T_BOX[bi];
        if (!range) continue;
        const boxProg = progressIn(sectionProg, range);
        boxProgsDbg[bi] = boxProg;
        const boxEased = easeOut(boxProg);

        // Box clip-path: bottom-inset animates from `calc(100% + 10px)`
        // (fully clipped — only the top edge visible, with the -10px
        // border letting selection-handles peek through) to `-10px`
        // (fully revealed, edges extended 10px past the box so handles
        // sit cleanly outside). Split into a % and a px term so the two
        // can be interpolated independently and combined via calc().
        const boxEl = boxRefs.current[bi];
        if (boxEl) {
          const bottomPercent = (1 - boxEased) * 100;
          const bottomPx = 10 - 20 * boxEased;
          boxEl.style.clipPath = `inset(-10px -10px calc(${bottomPercent}% + ${bottomPx}px) -10px)`;
        }

        const words = boxWordRefs.current[bi] ?? [];
        const n = words.length;
        const stagger = 0.04;
        const stretch = 1 + stagger * Math.max(n - 1, 0);
        for (let wi = 0; wi < n; wi++) {
          const el = words[wi];
          if (!el) continue;
          const wp = clamp01(boxProg * stretch - wi * stagger);
          const e = easeOut(wp);
          el.style.opacity = String(0.16 + 0.84 * e);
          el.style.filter = `blur(${(1 - e) * 9}px)`;
          if (e >= WORD_REVEAL_THRESHOLD) el.classList.add("is-revealed");
          else el.classList.remove("is-revealed");
        }

        // GIF clip-path: top-inset animates from 100% (clipped to
        // nothing) to 0% (fully revealed). Same eased curve as the box.
        const img = gifRefs.current[bi];
        if (img) {
          const topPercent = (1 - boxEased) * 100;
          img.style.clipPath = `inset(${topPercent}% 0 0 0)`;
        }
      }

      // Path segments
      // Dash pattern is in viewport px (non-scaling-stroke). Cache the
      // sampled rendered length per path; invalidate when the canvas
      // width changes so layout shifts (resize, breakpoint crossings,
      // any path-container stretch) keep dasharray aligned with the
      // path's actual rendered length.
      if (
        renderedLenCache.width !== stageRect.width &&
        stageRect.width > 0
      ) {
        // At narrow widths, pin each path's Y endpoints to the actual
        // box edges so paths visually originate from / land on the box
        // edges instead of overshooting into the next box or starting
        // below the previous box's bottom. X scale stays at 1 (path
        // X already compresses correctly via preserveAspectRatio="none").
        if (isNarrow) {
          const anchors = measureAnchors();
          if (anchors) {
            for (let i = 0; i < 4; i++) {
              const seg = segRefs.current[i];
              const spec = PATH_Y_ANCHORS[i];
              if (!seg || !spec) continue;
              const targetStart = anchors[spec.startAnchor];
              const targetEnd = anchors[spec.endAnchor];
              if (targetStart == null || targetEnd == null) continue;
              const origDelta = spec.origEnd - spec.origStart;
              if (origDelta === 0) continue;
              const scaleY = (targetEnd - targetStart) / origDelta;
              const dy = targetStart - scaleY * spec.origStart;
              seg.setAttribute(
                "transform",
                `translate(0 ${dy.toFixed(2)}) scale(1 ${scaleY.toFixed(4)})`,
              );
            }
          }
        } else {
          for (const seg of segRefs.current) {
            if (seg) seg.removeAttribute("transform");
          }
        }
        for (let i = 0; i < 4; i++) {
          const seg = segRefs.current[i];
          renderedLenCache.lengths[i] = seg ? measureRenderedLength(seg) : 0;
        }
        renderedLenCache.width = stageRect.width;
      }
      const pathProgsDbg: number[] = [0, 0, 0, 0];
      for (let i = 0; i < 4; i++) {
        const seg = segRefs.current[i];
        const screenLen = renderedLenCache.lengths[i] ?? 0;
        const range = T_PATH[i];
        if (!seg || screenLen === 0 || !range) continue;
        const prog = progressIn(sectionProg, range);
        pathProgsDbg[i] = prog;
        seg.style.strokeDasharray = String(screenLen);
        seg.style.strokeDashoffset = String(screenLen * (1 - easeOut(prog)));
      }

      // Terminal typing
      const terminalProg = progressIn(sectionProg, T_TERMINAL);
      const typingProg = clamp01(terminalProg / TERMINAL_TYPING_PORTION);
      const chars = Math.floor(typingProg * TERMINAL_TOTAL_CHARS);
      if (chars !== lastTypedChars) {
        let acc = 0;
        for (let ti = 0; ti < TERMINAL_TOKENS.length; ti++) {
          const tok = TERMINAL_TOKENS[ti]!;
          const start = acc;
          const end = acc + tok.text.length;
          acc = end;
          const el = terminalTokenRefs.current[ti];
          if (!el) continue;
          let visible: string;
          if (chars >= end) visible = tok.text;
          else if (chars > start) visible = tok.text.slice(0, chars - start);
          else visible = "";
          if (el.textContent !== visible) {
            el.textContent = visible;
          }
        }
        lastTypedChars = chars;
      }

      // Wand entry → tap on "magic". Hand rises from below the canvas and
      // settles with the wand tip at the magic word, ending in a small tap
      // recoil that triggers the zoom.
      const wandProg = progressIn(sectionProg, T_WAND);
      const wand = wandRef.current;
      const magicEl = terminalTokenRefs.current[5];

      // Scroll-driven dissolve progress. T_DISSOLVE spans from the
      // wand-impact moment (mid-recoil) through the end of the
      // post-tap-dissolve buffer phase, so the user scrolls the dust
      // apart in real time. Reseed the noise on each forward entry so
      // the dust pattern varies between plays.
      const dissolveScrollProg = progressIn(sectionProg, T_DISSOLVE);
      if (lastDissolveProgRef.current === 0 && dissolveScrollProg > 0) {
        reseed();
      }
      lastDissolveProgRef.current = dissolveScrollProg;

      // Lock the wand's rest position to the actual rendered "magic" span
      // so the tip lands on it regardless of viewport-driven prompt wrap.
      // Both rects come from getBoundingClientRect, which already accounts
      // for the canvas's translate, so their subtraction cancels it out
      // and we get clean canvas-relative pixels for inline top/left.
      if (wand && magicEl) {
        const magicRect = magicEl.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();
        const wandW = wand.offsetWidth;
        const wandH = wand.offsetHeight;
        if (wandW > 0) {
          const targetX = magicRect.left - canvasRect.left + magicRect.width / 2;
          const targetY = magicRect.top - canvasRect.top + magicRect.height / 2;
          wand.style.left = `${targetX - WAND_TIP_X * wandW}px`;
          wand.style.top = `${targetY - WAND_TIP_Y * wandH}px`;
        }
      }

      if (wand) {
        let translateY: number;
        let translateX: number;
        let opacity: number;
        let rotate: number;
        if (wandProg < 0.7) {
          // Rise into place
          const t = wandProg / 0.7;
          const e = easeOut(t);
          translateY = 360 * (1 - e); // % of own height — starts off-screen below
          translateX = -10 * (1 - e);
          opacity = e;
          rotate = -20 * (1 - e);
        } else if (wandProg < 0.85) {
          // Settled, holding rest
          translateY = 0;
          translateX = 0;
          opacity = 1;
          rotate = 0;
        } else {
          // Tap, three sub-phases so the strike reads as a clear motion
          // and the dissolve trigger lines up with the rightward snap:
          //   wind-up (t 0 → 0.5):  ease into peak left tilt + lift up
          //   strike  (t 0.5 → 0.65): rapid linear snap right + dip down
          //   settle  (t 0.65 → 1):  ease back from right tilt to rest
          // IMPACT_FRAC is set to wandProg 0.925 — that's the start of
          // the strike sub-phase, i.e., the moment the wand stops winding
          // up and starts swinging right.
          const t = (wandProg - 0.85) / 0.15;
          if (t < 0.5) {
            const wt = t / 0.5;
            const e = easeOut(wt);
            rotate = -8 * e;
            translateY = -10 * e;
          } else if (t < 0.65) {
            const st = (t - 0.5) / 0.15;
            rotate = -8 + 16 * st; // -8° → +8° (sweeps through upright)
            translateY = -10 + 14 * st; // -10 → +4 (overshoots downward)
          } else {
            const rt = (t - 0.65) / 0.35;
            const e = easeOut(rt);
            rotate = 8 * (1 - e);
            translateY = 4 * (1 - e);
          }
          translateX = 0;
          opacity = 1;
        }
        // The wand fades in lock-step with the prompt's dissolve so the
        // hand vanishes alongside the dust it cast. Uses the same
        // easeOutCubic + fadeStart envelope as the prompt opacity.
        const wandFade = (() => {
          const eased = 1 - Math.pow(1 - dissolveScrollProg, 3);
          if (eased <= dissolveConfig.fadeStart) return 0;
          return (eased - dissolveConfig.fadeStart) / (1 - dissolveConfig.fadeStart);
        })();
        opacity = Math.min(opacity, 1 - wandFade);
        wand.style.transform = `translate(${translateX}%, ${translateY}%) rotate(${rotate}deg)`;
        wand.style.opacity = String(opacity);
      }

      // Zoom — terminal scales up to fill viewport, surrounding canvas
      // content fades out, terminal interior fades during the latter half
      // of the zoom so by the end you're inside a clean dark space.
      const zoomProg = progressIn(sectionProg, T_ZOOM);
      const zoomEased = zoomProg * zoomProg;
      const term = terminalRef.current;
      const block = terminalBlockRef.current;
      if (term) {
        term.style.transform = `scale(${1 + zoomEased * (ZOOM_MAX_SCALE - 1)})`;
        term.style.setProperty("--zoom", String(zoomEased));
      }
      if (block) {
        if (zoomProg > 0) block.classList.add("is-zooming");
        else block.classList.remove("is-zooming");
        // Fade in over path 3 (box2 → terminal). On desktop this layers
        // on top of the natural canvas-translation reveal; on mobile —
        // where the canvas is too short to push the terminal off-screen
        // during heading/box phases — it's what hides the empty terminal
        // until the path approaches it.
        const terminalShowProg = progressIn(sectionProg, T_PATH[3]);
        block.style.opacity = String(terminalShowProg);
      }
      const fade = 1 - zoomEased;
      if (headingRef.current) headingRef.current.style.opacity = String(fade);
      for (const boxEl of boxRefs.current) if (boxEl) boxEl.style.opacity = String(fade);
      for (const img of gifRefs.current) if (img) img.style.opacity = String(fade);
      for (const seg of segRefs.current) if (seg) seg.style.opacity = String(fade);

      // Dark backdrop below the canvas. Steeper ramp than zoomEased so the
      // backdrop is fully opaque by the time the terminal stops covering
      // the viewport (scale ~1.5, zoomEased ~0.1) — prevents the paper
      // strip below the canvas from flashing in/out during the visible
      // portion of the zoom transition.
      const backdropVal = String(Math.min(1, zoomEased * 10));
      canvas.style.setProperty("--zoom-backdrop", backdropVal);

      // Dissolve animation (codepen-style). easeOutCubic on the
      // scroll-derived progress so the displacement starts gentle and
      // accelerates as the dust scatters apart. Reverses cleanly when
      // the user scrolls back.
      const easedDissolve = 1 - Math.pow(1 - dissolveScrollProg, 3);
      const dissolveScale = easedDissolve * dissolveConfig.maxDisplacement;
      const bloomFactor = 1 + dissolveConfig.bloomScale * easedDissolve;
      // Opacity stays at 1 until the eased progress hits fadeStart, then
      // drops linearly to 0 over the remaining fraction of the dissolve.
      let dissolveOpacity = 1;
      if (easedDissolve > dissolveConfig.fadeStart) {
        dissolveOpacity =
          1 -
          (easedDissolve - dissolveConfig.fadeStart) /
            (1 - dissolveConfig.fadeStart);
      }

      const promptEl = promptRef.current;
      const isDissolving = dissolveScrollProg > 0;
      if (promptEl) {
        if (isDissolving) {
          promptEl.classList.add("is-dissolving");
          promptEl.style.setProperty("--dissolve-bloom", String(bloomFactor));
          promptEl.style.setProperty(
            "--dissolve-opacity",
            String(dissolveOpacity),
          );
        } else {
          promptEl.classList.remove("is-dissolving");
          promptEl.style.removeProperty("--dissolve-bloom");
          promptEl.style.removeProperty("--dissolve-opacity");
        }
      }
      const dispMap = dissolveMapRef.current;
      if (dispMap) dispMap.setAttribute("scale", String(dissolveScale));

      // Carousel un-furl is one-shot: postCinematicTl tweens
      // postCinematicProgress.p from 0→1 on the very first forward
      // trigger and never reverses or replays. After that the carousel
      // stays at p=1 forever (continuous spin), and only .sub opacity
      // is animated on subsequent forward/reverse cycles.
      carouselRef.current?.setEntryProgress(postCinematicProgressRef.current.p);

      // Wheel-velocity nerf as the user approaches the parked zone.
      // Without this, a single fast wheel event can blast past parked in
      // one frame — onWheel sees isParked() === false at event time and
      // returns without firing reverse. By dropping wheelMultiplier as
      // .sub.top approaches viewport top, each wheel notch moves less,
      // giving the reverse trigger multiple frames to catch isParked()
      // === true. Outside the approach radius wheelMultiplier returns to
      // 1 so scrolling elsewhere on the page feels normal.
      const lenisForNerf = getLenis();
      if (lenisForNerf) {
        const subEl = document.querySelector(".sub");
        if (subEl) {
          const distance = Math.abs(subEl.getBoundingClientRect().top);
          const APPROACH_RADIUS = 400;
          const FLOOR = 0.3;
          const factor =
            distance < APPROACH_RADIUS
              ? FLOOR + (1 - FLOOR) * (distance / APPROACH_RADIUS)
              : 1;
          lenisForNerf.options.wheelMultiplier = factor;
          lenisForNerf.options.touchMultiplier = factor;
        }
      }

      // Debug timeline state — read by TimelineDebug component each
      // frame. Cheap object write; no React state involved.
      window.__tacDebug = {
        phasePlan: PHASE_PLAN,
        cumFrac: CUM_FRAC,
        totalPhaseVh: TOTAL_PHASE_VH,
        tDissolve: T_DISSOLVE,
        scrollSectionProg,
        sectionProg,
        triggered: triggeredRef.current,
        proxyT: progressProxyRef.current.t,
        headingProg,
        pathProgs: pathProgsDbg,
        boxProgs: boxProgsDbg,
        terminalProg,
        typingProg,
        chars,
        totalChars: TERMINAL_TOTAL_CHARS,
        wandProg,
        dissolveScrollProg,
        zoomProg,
        zoomEased,
        scale: 1 + zoomEased * (ZOOM_MAX_SCALE - 1),
        backdropOpacity: Math.min(1, zoomEased * 10),
      };
    };

    // Re-roll the big-noise seed so each play of the dissolve looks
    // different (matches codepen). Only the big noise needs reseeding —
    // the fine noise is so high-freq it averages out visually.
    const reseed = () => {
      const big = bigNoiseRef.current;
      if (big) big.setAttribute("seed", String(Math.floor(Math.random() * 1000)));
    };

    // engagePark: hard scroll lock during cinematic auto-play. Blocks
    // wheel/touch/keyboard scroll intent in BOTH directions; a scroll
    // guard snaps back to parkedY if anything slips through (Safari
    // momentum scroll, programmatic scrollTo from elsewhere, etc.). The
    // sole exit is the timeline's onComplete releasing the park — no
    // watchdog needed, .play()'s onComplete is reliable.
    const engagePark = (parkedY: number): (() => void) => {
      const lenis = getLenis();
      lenis?.stop();
      const onWheel = (e: WheelEvent) => e.preventDefault();
      const onTouchStart = (_e: TouchEvent) => {};
      const onTouchMove = (e: TouchEvent) => e.preventDefault();
      const onKeyDown = (e: KeyboardEvent) => {
        const k = e.key;
        if (
          k === "PageDown" || k === "PageUp" ||
          k === "ArrowDown" || k === "ArrowUp" ||
          k === "End" || k === "Home" ||
          k === " " || k === "Spacebar"
        ) {
          e.preventDefault();
        }
      };
      const onScrollGuard = () => {
        if (window.scrollY !== parkedY) window.scrollTo(0, parkedY);
      };
      window.addEventListener("wheel", onWheel, { passive: false });
      window.addEventListener("touchstart", onTouchStart, { passive: true });
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("scroll", onScrollGuard, { passive: true });
      return () => {
        window.removeEventListener("wheel", onWheel);
        window.removeEventListener("touchstart", onTouchStart);
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("scroll", onScrollGuard);
        lenis?.start();
      };
    };

    // Reverse-trigger proximity. Wheel/touch/keyboard gestures fire the
    // reverse animation whenever sub.top is at or below its parked
    // position by no more than this margin — i.e. the carousel is in
    // viewport or has been pushed DOWN from parked (the void state). The
    // old `Math.abs(sub.top) < 80` window missed users whose Lenis
    // momentum carried scroll past parked between wheel events: the
    // trigger could never fire because no wheel event landed while
    // sub.top sat inside [-80, 80].
    const REVERSE_PROXIMITY_PX = 80;
    // Threshold above which update() auto-fires the reverse trigger as a
    // safety net. Above this, the user has clearly scrolled UP past
    // parked into the dark void (cinematic visuals are all faded), so
    // even if Lenis is mid-glide with no wheel event firing, we react.
    const REVERSE_AUTO_TRIGGER_PX = 60;

    const fireReverse = () => {
      if (!cinematicTlRef.current) return;
      if (!releaseReverseTriggerRef.current) return; // already firing
      releaseReverseTriggerRef.current();
      releaseReverseTriggerRef.current = null;
      releaseParkRef.current = engagePark(window.scrollY);

      // Drop the content-revealed class so child blocks fade out via
      // their 700ms CSS transition while .sub is fading out below.
      document.querySelector(".sub")?.classList.remove("is-content-revealed");

      // 250ms .sub fade-out, then reverse the cinematic. Sequential so
      // the cinematic-reverse plays under a fully-cleared .sub. Carousel
      // is left at p=1 forever — never re-scrubbed.
      subFadeAnimRef.current?.pause();
      subFadeAnimRef.current = animate(".sub", {
        opacity: [1, 0],
        duration: 250,
        ease: "outQuad",
        onComplete: () => {
          cinematicTlRef.current?.reverse();
        },
      });
    };

    // installReverseTrigger: armed AFTER forward auto-play completes.
    // Listens for any up-scroll gesture while the carousel is at or
    // below its parked position. On fire: re-engage park + reverse the
    // cinematic. If user has scrolled DOWN past .sub (sub.top <
    // -REVERSE_PROXIMITY_PX), gestures pass through so the user can
    // freely browse content below.
    const installReverseTrigger = (): (() => void) => {
      let touchStartY: number | null = null;

      const isInRange = () => {
        const sub = document.querySelector(".sub");
        if (!sub) return false;
        // Fire when sub.top is at parked OR above parked (positive top
        // — user has scrolled up into the void). Only refuse when the
        // sub is well above the viewport (carousel above viewport top,
        // user is below carousel scrolling through real content).
        return sub.getBoundingClientRect().top > -REVERSE_PROXIMITY_PX;
      };

      const onWheel = (e: WheelEvent) => {
        if (e.deltaY >= 0) return;
        if (!isInRange()) return;
        e.preventDefault();
        fireReverse();
      };
      const onTouchStart = (e: TouchEvent) => {
        touchStartY = e.touches[0]?.clientY ?? null;
      };
      const onTouchMove = (e: TouchEvent) => {
        if (touchStartY == null) return;
        const y = e.touches[0]?.clientY;
        if (y == null) return;
        if (y - touchStartY <= 4) return;
        if (!isInRange()) return;
        e.preventDefault();
        fireReverse();
      };
      const onKeyDown = (e: KeyboardEvent) => {
        const k = e.key;
        if (k !== "ArrowUp" && k !== "PageUp" && k !== "Home") return;
        if (!isInRange()) return;
        e.preventDefault();
        fireReverse();
      };

      window.addEventListener("wheel", onWheel, { passive: false });
      window.addEventListener("touchstart", onTouchStart, { passive: true });
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("keydown", onKeyDown);
      return () => {
        window.removeEventListener("wheel", onWheel);
        window.removeEventListener("touchstart", onTouchStart);
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("keydown", onKeyDown);
      };
    };

    // Two peer timelines. autoplay:false — scroll position TRIGGERS
    // play()/reverse(); they do not scrub.
    //
    //  cinematicTl: progressProxyRef.current.t goes 0→1 with inOutQuad
    //  over 2500ms. update() reads this as the smoothed sectionProg for
    //  the [CUM_FRAC[10], 1] sub-range, rendering wand → dissolve →
    //  zoom. Plays on every forward trigger; reverses on every reverse
    //  trigger.
    //
    //  postCinematicTl: .sub opacity 0→1 + postCinematicProgress.p 0→1
    //  (the carousel un-furl). Plays ONLY on the first forward — chained
    //  by cinematicTl.onComplete the very first time. Never reversed,
    //  never replayed. After it completes once, carouselIntroDoneRef
    //  flips true and subsequent .sub crossfades go through a separate
    //  fire-and-forget animate() path.
    const cinematicTl = createTimeline({
      autoplay: false,
      onComplete: () => {
        const tl = cinematicTlRef.current;
        if (!tl) return;
        if (tl.reversed) {
          // Reverse complete: snap user to the start of hold-magic
          // (CUM_FRAC[9]) so they land in pre-wand scrub territory.
          // Buffer between snap point and forward trigger is the
          // hold-magic phase — ~13vh of real scroll (~115px @ 900px
          // viewport). Re-arm the forward trigger after a 400ms
          // cooldown that absorbs trackpad momentum from the gesture
          // that triggered reverse. .is-content-revealed was already
          // cleared synchronously in fire() at reverse start.
          releaseParkRef.current?.();
          releaseParkRef.current = null;
          const section = sectionRef.current;
          if (section) {
            const vh = window.innerHeight;
            const sectRect = section.getBoundingClientRect();
            const scrollableH = sectRect.height - vh;
            if (scrollableH > 0) {
              const sectTopPageY = window.scrollY + sectRect.top;
              const targetProg = CUM_FRAC[9]!;
              const targetY = sectTopPageY + targetProg * scrollableH;
              const lenis = getLenis();
              if (lenis) lenis.scrollTo(targetY, { immediate: true });
              else window.scrollTo(0, targetY);
            }
          }
          cooldownUntilRef.current = performance.now() + 400;
          triggeredRef.current = false;
        } else if (!carouselIntroDoneRef.current) {
          // First forward complete: chain into post-cinematic to drive
          // the carousel un-furl and the .sub fade-in. The park is kept
          // engaged through the un-furl and released in
          // postCinematicTl.onComplete below.
          postCinematicTlRef.current?.play();
        } else {
          // Subsequent forward complete: carousel is already settled,
          // so only fade .sub back in. The park is kept engaged through
          // the fade and released when the reverse trigger arms.
          // Re-add .is-content-revealed so the child blocks fade back
          // in via their 700ms CSS transition — handleEntryComplete
          // only fires on the first carousel un-furl, so we have to
          // toggle the class manually on subsequent passes.
          subFadeAnimRef.current?.pause();
          document.querySelector(".sub")?.classList.add("is-content-revealed");
          subFadeAnimRef.current = animate(".sub", {
            opacity: [0, 1],
            duration: 750,
            ease: "linear",
            onComplete: () => {
              releaseParkRef.current?.();
              releaseParkRef.current = null;
              releaseReverseTriggerRef.current = installReverseTrigger();
            },
          });
        }
        if (!raf) raf = requestAnimationFrame(update);
      },
      onUpdate: () => {
        if (!raf) raf = requestAnimationFrame(update);
      },
    });
    cinematicTl.add(
      progressProxyRef.current,
      { t: 1, duration: 2500, ease: "inOutQuad" },
      0,
    );
    cinematicTlRef.current = cinematicTl;

    // Post-cinematic: one-shot. .sub fades in over 750ms while the
    // carousel un-furls over 1500ms. By the moment the terminal interior
    // is fully dark (cinematicTl just ended), `.sub`'s matching #0d0e0e
    // surface fades underneath the still-dark terminal so the user reads
    // the transition as "falling into the dark interior of the expanded
    // terminal." On reverse triggers, postCinematicTl is never reversed
    // — the carousel intro is a one-shot animation.
    const postCinematicTl = createTimeline({
      autoplay: false,
      onComplete: () => {
        carouselIntroDoneRef.current = true;
        // Release the forward park before arming the reverse trigger,
        // so the user can scroll up to fire reverse without two layers
        // of park listeners stacking.
        releaseParkRef.current?.();
        releaseParkRef.current = null;
        releaseReverseTriggerRef.current = installReverseTrigger();
        if (!raf) raf = requestAnimationFrame(update);
      },
      onUpdate: () => {
        if (!raf) raf = requestAnimationFrame(update);
      },
    });
    postCinematicTl.add(
      ".sub",
      { opacity: [0, 1], duration: 750, ease: "linear" },
      0,
    );
    postCinematicTl.add(
      postCinematicProgressRef.current,
      { p: 1, duration: 2500, ease: "linear" },
      0,
    );
    postCinematicTlRef.current = postCinematicTl;

    const scheduleUpdate = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (raf) cancelAnimationFrame(raf);
      cinematicTlRef.current?.pause();
      cinematicTlRef.current?.revert();
      cinematicTlRef.current = null;
      postCinematicTlRef.current?.pause();
      postCinematicTlRef.current?.revert();
      postCinematicTlRef.current = null;
      subFadeAnimRef.current?.pause();
      subFadeAnimRef.current = null;
      releaseParkRef.current?.();
      releaseParkRef.current = null;
      releaseReverseTriggerRef.current?.();
      releaseReverseTriggerRef.current = null;
      triggeredRef.current = false;
      carouselIntroDoneRef.current = false;
      postCinematicProgressRef.current.p = 0;
      const lenisOnUnmount = getLenis();
      if (lenisOnUnmount) {
        lenisOnUnmount.options.wheelMultiplier = 1;
        lenisOnUnmount.options.touchMultiplier = 1;
      }
    };
  }, [isDesktop, isNarrow]);

  const renderTokens = (
    text: string,
    italicSet: Set<string>,
    sink: (HTMLSpanElement | null)[],
  ) => {
    const tokens = text.split(/(\s+)/);
    let visibleIdx = 0;
    return tokens.map((tok, i) => {
      if (tok === "" || /^\s+$/.test(tok)) return tok;
      const isItalic = italicSet.has(stripPunct(tok));
      const idx = visibleIdx++;
      return (
        <span
          key={i}
          ref={(el) => {
            sink[idx] = el;
          }}
          className={isItalic ? "tac2-word tac2-word--italic" : "tac2-word"}
        >
          {isItalic ? (
            <span className="tac2-italic-inner">{tok}</span>
          ) : (
            tok
          )}
        </span>
      );
    });
  };

  const headingItalicSet = new Set(HEADING_ITALICS);

  return (
    <section ref={sectionRef} className="tac2" aria-labelledby="tac2-title">
      {/* SVG filter that powers the dissolve on the terminal prompt. The
          feDisplacementMap's `scale` is animated each frame from JS — we
          keep `seed` and `baseFrequency` static so the noise pattern is
          stable (cheap), and only the displacement amount grows. */}
      <svg className="tac2-svg-defs" aria-hidden="true" focusable="false">
        <defs>
          {/*
            Dissolve filter — mirrors Bespalov's Thanos-snap codepen.

            Two turbulences combined: a low-frequency "big" noise that
            decides which direction chunks of the source drift, plus a
            high-frequency "fine" noise that adds per-pixel grain so the
            chunks read as dust. feFuncR/feFuncG sharpen the big noise
            into a strong directional vector field. feMerge unions both
            noises into one displacement map; feDisplacementMap then
            scatters the source by that combined field.

            JS animates `feDisplacementMap scale` from 0 → maxDisplacement
            with easeOutCubic; the prompt element separately blooms via a
            CSS transform and fades opacity from `fadeStart` of the
            duration onward. Big-noise `seed` is reset to a new random
            value on every fire for variety.

            Filter region is generous (-200%/-200%/500%/500%) so far-flung
            displaced pixels don't get clipped at the source's edges.
          */}
          <filter
            id="terminal-dissolve"
            x="-200%"
            y="-200%"
            width="500%"
            height="500%"
          >
            <feTurbulence
              ref={bigNoiseRef}
              type="fractalNoise"
              baseFrequency="0.015"
              numOctaves="1"
              seed="3"
              result="bigNoise"
            />
            <feComponentTransfer in="bigNoise" result="bigNoiseAdjusted">
              <feFuncR type="linear" slope="2" intercept="-0.4" />
              <feFuncG type="linear" slope="2" intercept="-0.4" />
            </feComponentTransfer>
            <feTurbulence
              type="fractalNoise"
              baseFrequency="1"
              numOctaves="2"
              result="fineNoise"
            />
            <feMerge result="combinedNoise">
              <feMergeNode in="bigNoiseAdjusted" />
              <feMergeNode in="fineNoise" />
            </feMerge>
            <feDisplacementMap
              ref={dissolveMapRef}
              in="SourceGraphic"
              in2="combinedNoise"
              scale="0"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>
      <div ref={stageRef} className="tac2-stage">
        <div ref={canvasRef} className="tac2-canvas">
          <div className="tac2-zoom-backdrop" aria-hidden="true" />
          {/* Heading lives inside .tac2-canvas at the artboard heading
              position (y≈129 of 3094, ~4.17%) so it translates with the
              canvas and the path's first segment emerges from its trailing
              edge ("changing…"). Fades out with --zoom-backdrop. */}
          <h2 ref={headingRef} id="tac2-title" className="tac2-heading">
            {renderTokens(HEADING, headingItalicSet, headingWordRefs.current)}
          </h2>
          <svg
            className="tac2-path"
            viewBox={`0 0 ${CANVAS_W} ${STORY_H}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {PATH_SEGMENTS.map((d, i) => (
              <path
                key={i}
                ref={(el) => {
                  segRefs.current[i] = el;
                }}
                d={d}
                fill="none"
                stroke="#5F8395"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>

          {STATEMENTS.map((s, i) => (
            <div
              key={i}
              ref={(el) => {
                boxRefs.current[i] = el;
              }}
              className={`tac2-statement tac2-statement--${s.side} ${s.rowClass}`}
            >
              <p className="tac2-statement-text">
                {renderTokens(s.text, new Set(s.italics), boxWordRefs.current[i] ?? [])}
              </p>
              <span className="tac2-handle tac2-handle--tl" aria-hidden="true" />
              <span className="tac2-handle tac2-handle--tr" aria-hidden="true" />
              <span className="tac2-handle tac2-handle--bl" aria-hidden="true" />
              <span className="tac2-handle tac2-handle--br" aria-hidden="true" />
            </div>
          ))}

          {STATEMENTS.map((s, i) => (
            <video
              key={i}
              ref={(el) => {
                gifRefs.current[i] = el;
              }}
              className={`tac2-gif ${s.gif.cls}`}
              src={s.gif.src}
              // muted + playsInline are required for iOS autoplay
              muted
              autoPlay
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
            />
          ))}

          <div ref={wandRef} className="tac2-wand" aria-hidden="true">
            <img src="/assets/wand-hand.png" alt="" />
          </div>

          <div ref={terminalBlockRef} className="tac2-terminal-block" aria-hidden="true">
            <div ref={terminalRef} className="tac-terminal">
              <div className="tac-terminal-bar">
                <span className="tac-terminal-dot tac-terminal-dot--red" />
                <span className="tac-terminal-dot tac-terminal-dot--yellow" />
                <span className="tac-terminal-dot tac-terminal-dot--green" />
              </div>
              <div className="tac-terminal-body">
                <div className="tac-terminal-cmd">johnnyappleseed@mac ~ % claude</div>
                <div className="tac-terminal-banner">
                  <img
                    className="tac-terminal-icon"
                    src="/assets/claude-pixel.png"
                    alt=""
                  />
                  <div className="tac-terminal-meta">
                    <div>
                      <strong>Claude Code</strong>{" "}
                      <span className="tac-terminal-dim">v2.1.119</span>
                    </div>
                    <div className="tac-terminal-dim">
                      Opus 4.7 (1M context) · Claude Max
                    </div>
                    <div className="tac-terminal-dim">/Users/johnnyappleseed</div>
                  </div>
                </div>
                <div className="tac-terminal-rule" />
                <div ref={promptRef} className="tac-terminal-prompt">
                  <span className="tac-terminal-arrow">&gt;</span>
                  <span className="tac-terminal-typed">
                    {TERMINAL_TOKENS.map((tok, i) =>
                      tok.italic ? (
                        <span
                          key={i}
                          ref={(el) => {
                            terminalTokenRefs.current[i] = el;
                          }}
                          className="tac-terminal-italic"
                        />
                      ) : (
                        <span
                          key={i}
                          ref={(el) => {
                            terminalTokenRefs.current[i] = el;
                          }}
                        />
                      ),
                    )}
                    <span className="tac-terminal-cursor" />
                  </span>
                </div>
                <div className="tac-terminal-shortcuts">? for shortcuts</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
